/**
 * Componente CorporateRichTextEditor - Editor de texto enriquecido
 * Wrapper de ReactQuill con estilos corporativos
 */

import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, FormControl, FormLabel, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface CorporateRichTextEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  minHeight?: number;
}

export const CorporateRichTextEditor: React.FC<CorporateRichTextEditorProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  minHeight = 200,
}) => {
  const theme = useTheme();

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean'],
      ],
    }),
    []
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'size',
    'color',
    'background',
    'list',
    'bullet',
    'blockquote',
    'code-block',
    'link',
  ];

  return (
    <FormControl fullWidth error={error}>
      <FormLabel
        sx={{
          mb: 1,
          fontWeight: 500,
          color: error ? theme.palette.error.main : theme.palette.text.primary,
        }}
      >
        {label}
      </FormLabel>
      <Box
        sx={{
          '& .quill': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${
              error ? theme.palette.error.main : theme.palette.divider
            }`,
            '&:hover': {
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.text.primary,
            },
            '&.ql-focused': {
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.primary.main,
              borderWidth: 2,
            },
          },
          '& .ql-toolbar': {
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            '& .ql-stroke': {
              stroke: theme.palette.text.primary,
            },
            '& .ql-fill': {
              fill: theme.palette.text.primary,
            },
            '& .ql-picker-label': {
              color: theme.palette.text.primary,
            },
          },
          '& .ql-container': {
            borderBottomLeftRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius,
            fontSize: '0.875rem',
            fontFamily: theme.typography.fontFamily,
            minHeight: `${minHeight}px`,
            '& .ql-editor': {
              minHeight: `${minHeight}px`,
              '&.ql-blank::before': {
                color: theme.palette.text.secondary,
                fontStyle: 'normal',
              },
            },
          },
        }}
      >
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={`Escribe ${label.toLowerCase()}...`}
        />
      </Box>
      {helperText && (
        <FormHelperText sx={{ mt: 1, mx: 0 }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

